package com.daytimegaming.ourdailyfinances.domain.fcm

import com.daytimegaming.ourdailyfinances.domain.repository.AuthRepository
import com.daytimegaming.ourdailyfinances.domain.repository.UserRepository
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.messaging.messaging
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch

data class FcmRemoteMessage(
    val messageId: String?,
    val title: String?,
    val body: String?,
    val data: Map<String, String> = emptyMap()
)

interface FcmManager {
    val fcmToken: StateFlow<String?>
    val incomingMessages: StateFlow<FcmRemoteMessage?>
    suspend fun getToken(): String?
    suspend fun subscribeToTopic(topic: String)
    suspend fun unsubscribeFromTopic(topic: String)
    fun initialize()
}

class FcmManagerImpl(
    private val userRepository: UserRepository,
    private val authRepository: AuthRepository,
) : FcmManager {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val _fcmToken = MutableStateFlow<String?>(null)
    override val fcmToken: StateFlow<String?> = _fcmToken.asStateFlow()

    private val _incomingMessages = MutableStateFlow<FcmRemoteMessage?>(null)
    override val incomingMessages: StateFlow<FcmRemoteMessage?> = _incomingMessages.asStateFlow()

    private var registeredTokenForUser: String? = null

    init {
        initialize()
    }

    override fun initialize() {
        scope.launch {
            try {
                val token = getToken()
                _fcmToken.value = token
            } catch (e: Exception) {
                // FCM token retrieval error handling
            }
        }

        // Register device token for user on login and unregister on logout
        scope.launch {
            authRepository.currentUser().combine(_fcmToken) { user, token ->
                user to token
            }.collect { (user, token) ->
                if (user != null && token != null) {
                    if (registeredTokenForUser != token) {
                        try {
                            userRepository.registerDeviceToken(token)
                            registeredTokenForUser = token
                        } catch (e: Exception) {
                            // Registration error handling
                        }
                    }
                } else if (user == null && registeredTokenForUser != null) {
                    val tokenToUnregister = registeredTokenForUser
                    registeredTokenForUser = null
                    if (tokenToUnregister != null) {
                        try {
                            userRepository.unregisterDeviceToken(tokenToUnregister)
                        } catch (e: Exception) {
                            // Unregister error handling
                        }
                    }
                }
            }
        }
    }

    override suspend fun getToken(): String? {
        return try {
            Firebase.messaging.getToken()
        } catch (e: Exception) {
            null
        }
    }

    override suspend fun subscribeToTopic(topic: String) {
        try {
            Firebase.messaging.subscribeToTopic(topic)
        } catch (e: Exception) {
            // Handle subscribe topic error
        }
    }

    override suspend fun unsubscribeFromTopic(topic: String) {
        try {
            Firebase.messaging.unsubscribeFromTopic(topic)
        } catch (e: Exception) {
            // Handle unsubscribe topic error
        }
    }
}
